import Canvas from "../Canvas";
import Layer from "../Layer";
import { ObjectId } from "../../helpers";
import { focusObject, insertAt, layersToTarget } from "../../utils";
import { GroupProps } from "./interfaces";

type Callback = () => void;

export default class Group {
  _id: string = new ObjectId().toString();
  functionalId: number = 1;
  canvas?: Canvas;
  name: string = "Group";
  parentObject?: Group;
  objects: (Layer | Group)[];
  parentId?: string;
  removable: boolean = true;

  callbackMap: Map<string, Set<Callback>> = new Map();
  modifiedKeysSet: Set<string> = new Set();

  constructor(objects: (Layer | Group)[] = [], options: Partial<GroupProps>) {
    objects.forEach((o) =>
      o instanceof Group ? (o.parentObject = this) : (o.groupObject = this),
    );
    this.set(options);
    this.objects = objects;
  }

  toObject() {
    this.modifiedKeysSet.add("parentId");

    return {
      _id: this._id,
      functionalId: this.functionalId,
      name: this.name,
      parentId: this.parentObject?._id,
      modifiedSettingKeys: [...this.modifiedKeysSet],
    };
  }

  set(options: Partial<Group>) {
    Object.assign(this, options);
  }

  on(eventName: string, callback: Callback): this {
    if (!this.callbackMap.has(eventName)) {
      this.callbackMap.set(eventName, new Set());
    }
    this.callbackMap.get(eventName)!.add(callback);
    return this;
  }

  off(eventName: string, callback?: Callback): this {
    const callbacks = this.callbackMap.get(eventName);
    if (!callbacks) {
      return this;
    }

    if (callback) {
      callbacks.delete(callback);
    } else {
      callbacks.clear();
    }

    if (callbacks.size === 0) {
      this.callbackMap.delete(eventName);
    }

    return this;
  }

  fire(eventName: string): this {
    const callbacks = this.callbackMap.get(eventName);
    if (!callbacks) {
      return this;
    }

    callbacks.forEach((cb) => {
      try {
        cb();
      } catch (err) {
        console.error(`Error in Group event listener for "${eventName}":`, err);
      }
    });

    return this;
  }

  getLayers() {
    const layers: Layer[] = [];
    this.objects.forEach((o) => {
      if (o instanceof Layer) layers.push(o);
      else layers.push(...o.getLayers());
    });
    return layers;
  }

  getSelection() {
    const activeObject = layersToTarget([...this.getLayers()].reverse());
    activeObject.groupObject = this;
    return activeObject;
  }

  isSelected(activeLayers: Layer[]) {
    const layers = this.getLayers();
    if (activeLayers.length === 0 || layers.length === 0) return false;
    return layers.every((l) => activeLayers.includes(l));
  }

  remove(...objects: (Layer | Group)[]) {
    const objToRemove = new Set(objects);
    const removedObjIdx: number[] = [];
    const newObjs: (Layer | Group)[] = [];
    this.objects.forEach((o, i) => {
      if (objToRemove.has(o)) removedObjIdx.push(i);
      else newObjs.push(o);
    });
    this.objects = newObjs;
    return removedObjIdx;
  }

  unGroup() {
    const parentObject = this?.parentObject;
    this.objects.forEach((o) => {
      if (o instanceof Group) {
        o.parentObject = parentObject;
      } else {
        o.groupObject = parentObject;
        if (!o.groupObject) focusObject(o, true);
      }
    });
    const idx = parentObject?.remove(this)?.at(0);
    if (parentObject && idx !== undefined) {
      parentObject.objects = insertAt(
        parentObject.objects,
        idx,
        ...this.objects,
      );
    }
    this.canvas?.groupMap.delete(this._id);
    this.canvas?.fire("group:removed", { target: this });
  }

  modify(key: string) {
    this.modifiedKeysSet.add(key);
  }
}
